import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchSavedQuotes, type SavedQuote } from "../api";
import { rampFor } from "../components/cover";
import { CloseIcon, ShareIcon } from "../components/icons";
import Placeholder from "../components/Placeholder";
import { useT } from "../i18n";
import {
  canvasMeasure,
  cardContent,
  coverMean,
  drawCard,
  loadCardFonts,
  LOOKS,
  shareText,
  SPEC,
} from "../quotes/quotePicture";
import { useSession } from "../session";

/**
 * My quotes on the web (requirement 0138): the passages the reader kept in
 * the app (0137), newest first, each shareable as a picture. Keeping, deleting
 * and the widget stay in the app; this list is only for sharing.
 */
export default function QuotesPage() {
  const { isSignedIn, isPending: sessionPending } = useSession();
  const t = useT();
  const quotes = useQuery({
    queryKey: ["saved-quotes", "me"],
    queryFn: fetchSavedQuotes,
    enabled: isSignedIn,
  });
  const [sharing, setSharing] = useState<SavedQuote | null>(null);

  if (!sessionPending && !isSignedIn) return <Navigate to="/" replace />;

  let body;
  if (quotes.isError) {
    body = (
      <div className="py-10 text-center text-sm text-muted" data-testid="quotes-error">
        <p>{t.quotes.failed}</p>
        <button
          type="button"
          onClick={() => void quotes.refetch()}
          className="mt-3 rounded-control px-3 py-1.5 text-ink ring-1 ring-line-strong hover:bg-sunk"
        >
          {t.quotes.retry}
        </button>
      </div>
    );
  } else if (sessionPending || quotes.isPending) {
    body = <Placeholder className="h-28 w-full rounded-card" />;
  } else if (quotes.data.items.length === 0) {
    body = (
      <p className="py-10 text-center text-sm text-muted" data-testid="quotes-empty">
        {t.quotes.empty}
      </p>
    );
  } else {
    body = (
      <ul className="divide-y divide-line" data-testid="quotes-list">
        {quotes.data.items.map((q) => (
          <li key={q.id} className="flex items-start gap-4 py-4" data-testid={`quote-${q.id}`}>
            <Thumb quote={q} />
            <div className="min-w-0 flex-1">
              <p dir="auto" className="font-serif text-[1.05rem] leading-relaxed text-ink">
                {q.text}
              </p>
              <p className="mt-1.5 truncate text-xs text-muted">
                {t.quotes.place(q.book_title, q.chapter_title ?? "")}
              </p>
            </div>
            <button
              type="button"
              data-testid={`quote-share-${q.id}`}
              aria-label={t.quotes.share}
              title={t.quotes.share}
              onClick={() => setSharing(q)}
              className="grid size-9 shrink-0 place-items-center rounded-full text-muted transition-colors hover:bg-sunk hover:text-ink"
            >
              <ShareIcon />
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="mb-6 text-[1.75rem] leading-tight font-semibold tracking-tight">
        {t.quotes.title}
      </h1>
      {body}
      {sharing && <ShareDialog quote={sharing} onClose={() => setSharing(null)} />}
    </main>
  );
}

function Thumb({ quote }: { quote: SavedQuote }) {
  const [from, to] = rampFor(quote.book_id);
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="aspect-[2/3] w-11 shrink-0 overflow-hidden rounded-[3px]"
      style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
    >
      {quote.cover && !failed && (
        <img
          src={quote.cover}
          alt=""
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      )}
    </div>
  );
}

type Made =
  | { state: "making" }
  | { state: "too-long" }
  | { state: "failed" }
  | { state: "ready"; blob: Blob; url: string };

/**
 * The card as it will be sent, with Download and, where the browser shares
 * files, Share. The web draws the Modern card: it has no Vintage style.
 */
function ShareDialog({ quote, onClose }: { quote: SavedQuote; onClose: () => void }) {
  const t = useT();
  const [made, setMade] = useState<Made>({ state: "making" });

  useEffect(() => {
    let url: string | null = null;
    let live = true;
    void (async () => {
      try {
        const mean = await coverMean(quote.book_id, quote.open);
        const measure = canvasMeasure();
        // Laid out once to know which faces to load, then again with them.
        await loadCardFonts(cardContent(quote, LOOKS.modern, mean, measure));
        const content = cardContent(quote, LOOKS.modern, mean, measure);
        if (content.size === null) {
          if (live) setMade({ state: "too-long" });
          return;
        }
        const canvas = document.createElement("canvas");
        drawCard(canvas, content);
        const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/png"));
        if (!blob) throw new Error("no picture");
        url = URL.createObjectURL(blob);
        if (live) setMade({ state: "ready", blob, url });
      } catch {
        if (live) setMade({ state: "failed" });
      }
    })();
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [quote]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const file =
    made.state === "ready" ? new File([made.blob], SPEC.fileName, { type: "image/png" }) : null;
  const canShare =
    !!file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/45 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.quotes.share}
        data-testid="quote-share-dialog"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-card bg-surface p-4 shadow-[0_16px_40px_-16px_rgb(0_0_0/0.45)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t.quotes.close}
          className="absolute end-2 top-2 grid size-8 place-items-center rounded-full text-muted hover:bg-sunk"
        >
          <CloseIcon />
        </button>
        {made.state === "making" && (
          <p className="py-16 text-center text-sm text-muted">{t.quotes.making}</p>
        )}
        {made.state === "too-long" && (
          <p
            data-testid="quote-too-long"
            className="px-4 py-12 text-center text-[0.95rem] leading-relaxed"
          >
            {t.quotes.tooLong}
          </p>
        )}
        {made.state === "failed" && (
          <p className="px-4 py-12 text-center text-sm text-muted">{t.quotes.failedPicture}</p>
        )}
        {made.state === "ready" && (
          <>
            <img
              src={made.url}
              alt={quote.text}
              data-testid="quote-picture"
              className="mt-8 w-full rounded-[4px] ring-1 ring-line"
            />
            <div className="mt-4 flex justify-center gap-3">
              <a
                href={made.url}
                download={SPEC.fileName}
                data-testid="quote-download"
                className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage hover:bg-sage-deep"
              >
                {t.quotes.download}
              </a>
              {canShare && (
                <button
                  type="button"
                  data-testid="quote-share-send"
                  onClick={() =>
                    void navigator
                      .share({
                        files: [file!],
                        text: shareText(quote.book_title, quote.author, quote.book_url),
                      })
                      .catch(() => {})
                  }
                  className="rounded-control px-4 py-2 text-sm font-medium text-ink ring-1 ring-line-strong hover:bg-sunk"
                >
                  {t.quotes.shareTo}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
